import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { giftorPublic, giftorDeclarationUrl } from "@/lib/giftorApi";
import { SignaturePad } from "@/components/app/esign/SignaturePad";
import { Gift, FileSignature, Banknote, IdCard, CheckCircle2, Loader2, ExternalLink, Upload, Trash2, ShieldCheck } from "lucide-react";

const NAVY = "#100f5d";

function fmtGBP(n) { try { return `£${Number(n).toLocaleString("en-GB")}`; } catch { return `£${n}`; } }

export default function GiftorPortalPage() {
  const { inviteToken } = useParams();
  const [token, setToken] = useState(null);        // giftor JWT (in memory)
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [me, setMe] = useState(null);
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const requestCode = async () => {
    setSending(true);
    try {
      const { data } = await giftorPublic.post(`/giftor/auth/request-otp`, { invite_token: inviteToken });
      setCodeSent(true);
      if (data.debug_code) setCode(data.debug_code); // preview/testing echo only
      toast.success("We've emailed you a 6-digit code.");
    } catch { toast.error("Could not send a code. Please check your link."); }
    finally { setSending(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await giftorPublic.post(`/giftor/auth/verify-otp`, { invite_token: inviteToken, code: code.trim() });
      setToken(data.access_token);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid or expired code."); }
    finally { setVerifying(false); }
  };

  const loadMe = useCallback(async () => {
    if (!token) return;
    try { const { data } = await giftorPublic.get(`/giftor/me`, { headers: { Authorization: `Bearer ${token}` } }); setMe(data); }
    catch { /* token expired */ setToken(null); toast.error("Your session expired — please verify again."); }
  }, [token]);

  useEffect(() => { loadMe(); }, [loadMe]);

  // ---- Auth gate ----
  if (!token) {
    return (
      <Shell>
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mt-10" data-testid="giftor-auth-card">
          <div className="w-12 h-12 rounded-full bg-[#100f5d]/10 flex items-center justify-center mb-4"><Gift className="w-6 h-6 text-[#100f5d]" /></div>
          <h1 className="text-xl font-semibold text-slate-900">Giftor verification</h1>
          <p className="text-sm text-slate-600 mt-1">To keep things secure, we'll email you a one-time code before you continue.</p>
          {!codeSent ? (
            <button onClick={requestCode} disabled={sending} className="mt-6 w-full py-2.5 rounded-lg bg-[#100f5d] text-white text-sm font-medium hover:bg-[#1a1980] inline-flex items-center justify-center gap-2" data-testid="giftor-request-code">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Email me a code
            </button>
          ) : (
            <div className="mt-6 space-y-3">
              <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="Enter 6-digit code"
                className="w-full text-center tracking-[0.5em] text-lg border border-slate-300 rounded-lg px-3 py-2.5" data-testid="giftor-code-input" />
              <button onClick={verify} disabled={verifying || code.length !== 6} className="w-full py-2.5 rounded-lg bg-[#100f5d] text-white text-sm font-medium hover:bg-[#1a1980] disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="giftor-verify-code">
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify & continue
              </button>
              <button onClick={requestCode} className="w-full text-xs text-slate-500 hover:text-slate-800" data-testid="giftor-resend-code">Resend code</button>
            </div>
          )}
        </div>
      </Shell>
    );
  }

  if (!me) return <Shell><div className="flex items-center justify-center py-20"><Loader2 className="w-7 h-7 text-slate-400 animate-spin" /></div></Shell>;

  return (
    <Shell>
      <div className="max-w-2xl mx-auto mt-6" data-testid="giftor-portal">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-4">
          <div className="flex items-center gap-2"><Gift className="w-5 h-5 text-[#c87d3a]" /><h1 className="text-lg font-semibold text-slate-900">Hello {me.name}</h1></div>
          <p className="text-sm text-slate-600 mt-1">You're gifting <strong>{fmtGBP(me.gift_amount)}</strong> towards <strong>{me.buyer_name}</strong>'s purchase of <strong>{me.property_address}</strong>. Please complete the three steps below.</p>
        </div>

        <DeclarationCard token={token} me={me} onDone={loadMe} />
        <FundsCard token={token} authHeader={authHeader} me={me} onDone={loadMe} />
        <IdCard_ token={token} authHeader={authHeader} me={me} onDone={loadMe} />

        {me.status === "complete" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3 mt-4" data-testid="giftor-all-done">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <p className="text-sm text-emerald-800 font-medium">All done — thank you! There's nothing further for you to do. Your solicitor has everything they need.</p>
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#100f5d] text-white">
        <div className="h-[3px] bg-[#c87d3a]" />
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="h-8 w-auto" onError={(e) => { e.target.style.display = "none"; }} />
          <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/10 text-[#e89e63]">Giftor portal</span>
        </div>
      </header>
      <main className="px-4 pb-16">{children}</main>
    </div>
  );
}

function CardShell({ done, Icon, title, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${done ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>{done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-4 h-4" />}</div>
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {done && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ml-auto">Done</span>}
      </div>
      {children}
    </div>
  );
}

function DeclarationCard({ token, me, onDone }) {
  const done = me.steps?.declaration;
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const sign = async () => {
    if (!sig) { toast.error("Please draw or type your signature first."); return; }
    setBusy(true);
    try { await giftorPublic.post(`/giftor/declaration/sign`, { signature: sig }, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Declaration signed."); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not sign the declaration."); }
    finally { setBusy(false); }
  };
  return (
    <CardShell done={done} Icon={FileSignature} title="1. Gifted deposit declaration" testid="giftor-card-declaration">
      {me.declaration_content && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-3 max-h-72 overflow-y-auto" data-testid="giftor-declaration-text">
          <p className="text-sm font-semibold text-slate-900">{me.declaration_content.title}</p>
          <p className="text-[11px] text-slate-500 mb-2">{me.declaration_content.firm}</p>
          {(me.declaration_content.intro || []).map((p, i) => (
            <p key={i} className="text-[13px] text-slate-700 leading-relaxed mb-2">{p}</p>
          ))}
          <p className="text-[13px] font-semibold text-slate-800 mt-1 mb-1">I declare and confirm that:</p>
          <ul className="list-disc pl-5 space-y-1">
            {(me.declaration_content.clauses || []).map((c, i) => (
              <li key={i} className="text-[13px] text-slate-700 leading-relaxed">{c}</li>
            ))}
          </ul>
        </div>
      )}
      <a href={giftorDeclarationUrl(token)} target="_blank" rel="noreferrer" className="text-sm text-[#100f5d] hover:underline inline-flex items-center gap-1.5 mb-3" data-testid="giftor-view-declaration">
        <ExternalLink className="w-4 h-4" /> {done ? "View your signed declaration (PDF)" : "Open as PDF"}
      </a>
      {!done && (
        <div>
          <p className="text-xs text-slate-500 mb-2">Having read the declaration above, please sign below to confirm the gift is unconditional and non-repayable.</p>
          <SignaturePad defaultName={me.name} onChange={setSig} />
          <button onClick={sign} disabled={busy} className="mt-3 py-2 px-4 rounded-lg bg-[#100f5d] text-white text-sm font-medium hover:bg-[#1a1980] disabled:opacity-50 inline-flex items-center gap-2" data-testid="giftor-sign-declaration">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Sign declaration
          </button>
        </div>
      )}
    </CardShell>
  );
}

function FundsCard({ token, authHeader, me, onDone }) {
  const done = me.steps?.funds;
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await giftorPublic.post(`/giftor/proof-of-funds`, fd, { headers: { ...authHeader, "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded."); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async (id) => {
    try { await giftorPublic.delete(`/giftor/proof-of-funds/${id}`, { headers: authHeader }); onDone(); }
    catch { toast.error("Could not remove the file."); }
  };
  return (
    <CardShell done={done} Icon={Banknote} title="2. Proof of funds" testid="giftor-card-funds">
      <p className="text-xs text-slate-500 mb-2">Upload evidence of the money you're gifting — e.g. recent bank or savings statements (PDF, JPG or PNG).</p>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple className="hidden" data-testid="giftor-funds-input" onChange={(e) => upload(Array.from(e.target.files || []))} />
      <button onClick={() => inputRef.current?.click()} disabled={busy} className="py-2 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:border-[#100f5d] inline-flex items-center gap-2" data-testid="giftor-funds-upload">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload files
      </button>
      {(me.funds || []).length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="giftor-funds-list">
          {me.funds.map((f) => (
            <li key={f.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-md px-3 py-1.5">
              <span className="truncate text-slate-700">{f.filename}</span>
              <button onClick={() => remove(f.id)} className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`giftor-funds-remove-${f.id}`}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </CardShell>
  );
}

function IdCard_({ token, authHeader, me, onDone }) {
  const done = me.steps?.id_check;
  const [busy, setBusy] = useState(false);
  const status = me.didit?.status;
  const start = async () => {
    setBusy(true);
    try {
      const { data } = await giftorPublic.post(`/giftor/didit`, {}, { headers: authHeader });
      if (data.url) { window.open(data.url, "_blank", "noopener"); toast.info("Complete your ID check in the new tab, then return here."); }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the ID check."); }
    finally { setBusy(false); }
  };
  // Light polling while an ID check is underway.
  useEffect(() => {
    if (done) return;
    const started = status && !["not_started", "Not Started", null, undefined].includes(status);
    if (!started) return;
    const t = setInterval(onDone, 6000);
    return () => clearInterval(t);
  }, [done, status, onDone]);

  return (
    <CardShell done={done} Icon={IdCard} title="3. Identity & AML check" testid="giftor-card-id">
      {done ? (
        <p className="text-sm text-emerald-700 inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Your identity has been verified. Thank you.</p>
      ) : (
        <div>
          <p className="text-xs text-slate-500 mb-2">A quick, secure identity check (photo ID + a selfie). You can use your phone's camera.</p>
          <button onClick={start} disabled={busy} className="py-2 px-4 rounded-lg bg-[#100f5d] text-white text-sm font-medium hover:bg-[#1a1980] disabled:opacity-50 inline-flex items-center gap-2" data-testid="giftor-start-id">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <IdCard className="w-4 h-4" />} {status && !["not_started", "Not Started"].includes(status) ? "Continue ID check" : "Start ID check"}
          </button>
          {status && !["not_started", "Not Started"].includes(status) && <p className="text-[11px] text-slate-400 mt-2">Status: {status}</p>}
        </div>
      )}
    </CardShell>
  );
}
